import React from 'react';
import StreakCard from './StreakCard';
import CTACard from './CTACard';
import {
  LEARNING_PLAN_UNIT_TYPE,
  LearningPlanUnit,
} from '../LearningPlan/LearningPlanUnit';
import {
  getAppliedProjectUrl,
  getProsumerActivityUrl,
  getProsumerDashboardUrl,
  projectsDashboardPath,
} from '../../routing';
import styled from 'styled-components';
import { useHistory } from 'react-router-dom';
import Button from '../elements/Button';
import { H3 } from '../elements/Text';
import LearningPlan from '../LearningPlan/LearningPlan';
import {
  useGetProsumerProfile,
  useProjectsDashboard,
} from '../../hooks/use-hydra';
import { AppliedProjectStat, ProsumerContentModule } from '../../hydra';
import useAuth from '../../hooks/use-auth';
import { useMutation } from 'react-query';
import { AppliedProjects } from '../../pages/ProjectsDashboard';
import { toast } from 'react-toastify';
import {
  joinProsumerContent,
  LearningPlan as LearningPlanType,
} from '../../hydra';
import ProsumerContentModuleCard from './ProsumerContentModuleCard';

const Row = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(285px, 1fr));
  gap: 32px;
  margin-top: 36px;

  @media screen and (max-width: 664px) {
    display: flex;
    flex-direction: column;
  }
`;
const CenteredRow = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 36px;
`;

const CardsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(285px, 1fr));
  gap: 32px;
  margin: 32px 0px;
`;

export default function ProsumerHome() {
  const history = useHistory();
  const { value: prosumerProfile, loading: isLoadingProsumer } =
    useGetProsumerProfile();
  const { value: projects, loading: isLoadingProjects } =
    useProjectsDashboard();

  const [shouldShowAllModules, setShouldShowAllModules] = React.useState(false);

  const prosumerContentModules = prosumerProfile?.contentModules ?? [];
  const appliedProjects = projects ?? [];
  const learningPlan = prosumerProfile?.profile.learning_plan;
  const shouldShowLearningPlan =
    prosumerProfile?.profile.learning_plan &&
    !isLoadingProjects &&
    !isLoadingProsumer;

  const prosumerLearnerPlan = useGetLearningPlan({
    learningPlan,
    projects: appliedProjects,
    contentModules: prosumerContentModules,
  });

  const allTracks: Array<{
    type: 'prosumer';
    module: ProsumerContentModule;
  }> = [
    ...prosumerContentModules.map(
      (module) => ({ module, type: 'prosumer' } as const)
    ),
  ];

  return (
    <React.Fragment>
      <Progress plan={prosumerLearnerPlan} />
      {shouldShowLearningPlan && (
        <LearningPlan
          infoText={`✨ This unique learning plan is based on your goals and profile. It will update as Enki learns more about you!`}
          learningPlan={prosumerLearnerPlan}
        />
      )}

      {appliedProjects.length > 0 && (
        <>
          <H3>Projects</H3>
          <AppliedProjects
            appliedProjects={appliedProjects.slice(0, 3)}
            onGoToProject={(slug) => history.push(getAppliedProjectUrl(slug))}
          />

          <CenteredRow>
            <Button
              onClick={() => history.push(projectsDashboardPath)}
              label={'Show All Projects'}
            />
          </CenteredRow>
        </>
      )}
      <H3>Modules</H3>
      <Modules
        allTracks={allTracks.slice(0, shouldShowAllModules ? undefined : 3)}
      />
      {allTracks.length > 3 && (
        <CenteredRow>
          <Button
            onClick={() => setShouldShowAllModules(!shouldShowAllModules)}
            label={shouldShowAllModules ? 'Hide Modules' : 'Show All Modules'}
          />
        </CenteredRow>
      )}
    </React.Fragment>
  );
}

function Progress({ plan }: { plan: LearningPlanUnit[] }) {
  const history = useHistory();

  const nextPlanItem =
    plan.find((item) => !item.isCompleted) ?? plan[plan.length - 1];

  if (!nextPlanItem) {
    return <StreakCard />;
  }

  return (
    <Row>
      <CTACard
        title={nextPlanItem.moduleTitle}
        nextItemTitle={nextPlanItem.nextItemTitle}
        description={nextPlanItem.description}
        imageUrl={nextPlanItem.imageUrl}
        dashboardUrl={nextPlanItem.dashboardUrl}
        progressText={nextPlanItem.progressDescription}
        primaryButton={{
          ctaLabel: !nextPlanItem.isCompleted
            ? nextPlanItem.isInProgress
              ? 'Continue'
              : 'Start'
            : 'Dashboard',
          onCTAClick: nextPlanItem.onClick,
        }}
        secondaryButton={
          nextPlanItem.type === LEARNING_PLAN_UNIT_TYPE.CONTENT_MODULE &&
          !nextPlanItem.isCompleted
            ? {
                ctaLabel: 'Dashboard',
                onCTAClick: () => {
                  history.push(nextPlanItem.dashboardUrl ?? '');
                },
              }
            : null
        }
      />
      <StreakCard />
    </Row>
  );
}

function useGetLearningPlan({
  learningPlan,
  projects,
  contentModules,
}: {
  learningPlan: LearningPlanType | null | undefined;
  projects: Array<AppliedProjectStat>;
  contentModules: Array<ProsumerContentModule>;
}): Array<LearningPlanUnit> {
  const { getTokenSilently } = useAuth();
  const history = useHistory();

  const joinModuleMutation = useMutation({
    mutationFn: async ({
      unit,
      module,
    }: {
      unit: LearningPlanType[0];
      module: ProsumerContentModule;
    }) => {
      if (!(unit.type === 'module')) {
        throw new Error('Invalid learning unit type');
      }
      try {
        const token = await getTokenSilently();
        const nextSlugs = await joinProsumerContent(token, {
          contentId: module.contentId,
        });
        return nextSlugs;
      } catch (err) {
        toast.error('Something went wrong');
        reportError(err);
      }
    },
  });

  const learningPlanUnits: Array<LearningPlanUnit> = (learningPlan ?? [])
    .map((unit) => {
      if (unit.type === 'project') {
        const project = projects.find((p) => p.slug === unit.slug);
        if (!project) {
          return null;
        }

        const completedQuestions = project.completedQuestions ?? 0;
        const isCompleted = completedQuestions === project.question_count;
        const isInProgress =
          completedQuestions > 0 && completedQuestions < project.question_count;
        const progressDescription =
          completedQuestions === project.question_count || !completedQuestions
            ? `${project.question_count} exercises`
            : `${completedQuestions}/${project.question_count} questions`;

        return {
          type: LEARNING_PLAN_UNIT_TYPE.PROJECT,
          unitTitle: project?.name ?? '',
          nextItemTitle: project?.name ?? '',
          moduleTitle: project?.topic ?? '',
          subtitle: 'PROJECT',
          slug: unit.slug,
          imageUrl: project?.image_url ?? '',
          description: project?.description ?? '',
          reasoning: unit.reasoning,
          progressDescription,
          isCompleted,
          isInProgress,
          onClick: async () => {
            history.push(getAppliedProjectUrl(project.slug));
          },
          dashboardUrl: getAppliedProjectUrl(project.slug) ?? '',
          isLocked: false,
        };
      }

      const module = contentModules.find((m) => m.contentSlug === unit.slug);
      if (!module) {
        return null;
      }

      const completedLessons = module.completedLessonsCount ?? 0;
      const isCompleted = completedLessons === module.lessonsCount;
      const isInProgress =
        completedLessons > 0 && completedLessons < module.lessonsCount;

      const progressDescription =
        completedLessons === module.lessonsCount || !completedLessons
          ? `${module.lessonsCount} lessons`
          : `${completedLessons}/${module.lessonsCount} lessons`;

      return {
        type: LEARNING_PLAN_UNIT_TYPE.CONTENT_MODULE,
        unitTitle: module.contentTitle,
        nextItemTitle: module.nextActivityName || module.contentTitle,
        moduleTitle: module.contentTitle,
        subtitle: 'MODULE',
        slug: unit.slug,
        imageUrl: module.contentImageUrl ?? '',
        description: module.contentDescription ?? '',
        reasoning: unit.reasoning,
        progressDescription,
        isCompleted,
        isInProgress,
        onClick: async () => {
          if (!module.isJoined) {
            const nextSlugs = await joinModuleMutation.mutateAsync({
              unit,
              module,
            });
            if (nextSlugs?.next_activity_slug && nextSlugs?.next_mission_slug) {
              history.push(
                getProsumerActivityUrl(
                  nextSlugs.next_mission_slug,
                  nextSlugs.next_activity_slug
                )
              );
            }
          } else if (module.nextMissionSlug && module.nextActivitySlug) {
            history.push(
              getProsumerActivityUrl(
                module.nextMissionSlug,
                module.nextActivitySlug
              )
            );
          } else {
            // When they have already completed the entire module
            history.push(getProsumerDashboardUrl(module.contentSlug));
          }
        },
        dashboardUrl: getProsumerDashboardUrl(module.contentSlug) ?? '',
        isLocked: false,
      };
    })
    .filter((unit): unit is NonNullable<typeof unit> => unit !== null);

  return learningPlanUnits;
}

function Modules({
  allTracks,
}: {
  allTracks: Array<{
    type: 'prosumer';
    module: ProsumerContentModule;
  }>;
}) {
  return (
    <CardsContainer>
      {allTracks.map((t) => {
        if (t.type === 'prosumer') {
          return (
            <ProsumerContentModuleCard
              key={`prosumer-content-module-${t.module.contentId}`}
              module={t.module}
            />
          );
        }
        return <p>hi</p>;
      })}
    </CardsContainer>
  );
}
